import { isSpreadsheetColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumns.helpers';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { createStateSelector } from '~/_shared/utils/memoize/createSelector';
import { notNull } from '~/_shared/utils/typeGuards';
import type { AppState } from '~/store/app.store';
import {
  type Chart, type ChartMetric, type ChartWithRefMetrics, isChartMetricLocationCountRef,
  isChartMetricSpreadsheetColumnRef,
} from './mapSettingsCharts.types';

const mapSettingsChartsItemsSelector = (state: AppState) => state.map.mapSettings.data.charts.items;
const mapSettingsChartsColumnsMapSelector = (state: AppState) => state.map.mapSettings.data.charts.columnsMap;

export const mapSettingsChartsSelector = createStateSelector([
  mapSettingsChartsItemsSelector,
  mapSettingsChartsColumnsMapSelector,
], (items, columnsMap) => {
  const resolveRefColumnsInMetrics = (items: ChartWithRefMetrics['metrics']): ReadonlyArray<ChartMetric> =>
    items.map(metric => {
      if (isChartMetricSpreadsheetColumnRef(metric)) {
        const columnId = columnsMap[metric.column];
        if (isSpreadsheetColumnId(columnId)) {
          return {
            ...metric,
            column: columnId,
          } satisfies ChartMetric;
        }
        return null;
      }
      if (isChartMetricLocationCountRef(metric)) {
        const column = metric.column ? columnsMap[metric.column] : undefined;
        const spreadsheetId = metric.spreadsheetId ? columnsMap[metric.spreadsheetId]?.spreadsheetId : undefined;

        return {
          ...metric,
          column: isSpreadsheetColumnId(column) ? column : undefined,
          spreadsheetId: spreadsheetId ?? undefined,
        } satisfies ChartMetric;
      }
      return metric;
    }).filter(notNull);

  return items.map(chart => ({
    ...chart,
    metrics: resolveRefColumnsInMetrics(chart.metrics),
  } as Chart));
});

export const useMapSettingsChartsSelector = () => useSelector(mapSettingsChartsSelector);
